import * as ToplistingApiService from '@/api/toplistingService';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import {
  runRegionalHighlightOperation,
  setRegionalHighlightApiStatus,
  type RegionalHighlightOperation,
  type RegionalHighlightRefs,
} from '@/stores/toplisting/regionalHighlight';
import { ApiCallStatus } from '@/stores/types/insertions';
import type { RegionalHighlightPurchaseOrderRequest } from '@/types/regionalHighlight';

const requireActiveAgencyId = (
  state: RegionalHighlightRefs,
  operation: RegionalHighlightOperation,
): string | undefined => {
  const activeAgencyId = useAgencyStore().activeAgencyId;
  if (!activeAgencyId) {
    setRegionalHighlightApiStatus(state, { operation, status: ApiCallStatus.ERROR });
    return undefined;
  }
  return activeAgencyId;
};

interface RegionalHighlightPurchaseActions {
  fetchRegionalHighlightPurchaseOptions: () => Promise<void>;
  fetchRegionalHighlightPurchaseOrders: () => Promise<void>;
  createRegionalHighlightPurchaseOrder: (
    request: RegionalHighlightPurchaseOrderRequest,
  ) => Promise<void>;
  extendRegionalHighlightSlot: (payload: {
    slotId: string;
    request: RegionalHighlightPurchaseOrderRequest;
  }) => Promise<void>;
  setRegionalHighlightAutoRenew: (payload: { slotId: string; autoRenew: boolean }) => Promise<void>;
}

export const createRegionalHighlightPurchaseActions = (
  state: RegionalHighlightRefs,
  fetchRegionalHighlightSlots: (listingId?: string) => Promise<void>,
): RegionalHighlightPurchaseActions => {
  const fetchRegionalHighlightPurchaseOptions = async (): Promise<void> => {
    const activeAgencyId = requireActiveAgencyId(state, 'purchaseOptions');
    if (!activeAgencyId) {
      return;
    }

    await runRegionalHighlightOperation(state, 'purchaseOptions', activeAgencyId, async () => {
      const options =
        await ToplistingApiService.fetchRegionalHighlightPurchaseOptions(activeAgencyId);
      return () => {
        state.regionalHighlightPurchaseOptions.value = options;
      };
    });
  };

  const fetchRegionalHighlightPurchaseOrders = async (): Promise<void> => {
    const activeAgencyId = requireActiveAgencyId(state, 'purchaseOrders');
    if (!activeAgencyId) {
      return;
    }

    await runRegionalHighlightOperation(state, 'purchaseOrders', activeAgencyId, async () => {
      const orders =
        await ToplistingApiService.fetchRegionalHighlightPurchaseOrders(activeAgencyId);
      return () => {
        state.regionalHighlightPurchaseOrders.value = orders;
      };
    });
  };

  const createRegionalHighlightPurchaseOrder = async (
    request: RegionalHighlightPurchaseOrderRequest,
  ): Promise<void> => {
    const activeAgencyId = requireActiveAgencyId(state, 'createPurchaseOrder');
    if (!activeAgencyId) {
      return;
    }

    const succeeded = await runRegionalHighlightOperation(
      state,
      'createPurchaseOrder',
      activeAgencyId,
      async () => {
        const order = await ToplistingApiService.createRegionalHighlightPurchaseOrder(
          activeAgencyId,
          request,
        );
        return () => {
          state.regionalHighlightLastPurchaseOrder.value = order;
        };
      },
    );
    if (succeeded) {
      await fetchRegionalHighlightPurchaseOrders();
    }
  };

  const extendRegionalHighlightSlot = async (payload: {
    slotId: string;
    request: RegionalHighlightPurchaseOrderRequest;
  }): Promise<void> => {
    const activeAgencyId = requireActiveAgencyId(state, 'extendSlot');
    if (!activeAgencyId) {
      return;
    }

    const succeeded = await runRegionalHighlightOperation(
      state,
      'extendSlot',
      activeAgencyId,
      async () => {
        const order = await ToplistingApiService.extendRegionalHighlightSlot(
          activeAgencyId,
          payload.slotId,
          payload.request,
        );
        return () => {
          state.regionalHighlightLastPurchaseOrder.value = order;
        };
      },
    );
    if (succeeded) {
      await fetchRegionalHighlightSlots();
      await fetchRegionalHighlightPurchaseOrders();
    }
  };

  const setRegionalHighlightAutoRenew = async (payload: {
    slotId: string;
    autoRenew: boolean;
  }): Promise<void> => {
    const activeAgencyId = requireActiveAgencyId(state, 'autoRenew');
    if (!activeAgencyId) {
      return;
    }

    const succeeded = await runRegionalHighlightOperation(
      state,
      'autoRenew',
      activeAgencyId,
      async () => {
        await ToplistingApiService.setRegionalHighlightAutoRenew(
          activeAgencyId,
          payload.slotId,
          payload.autoRenew,
        );
        return undefined;
      },
    );
    if (succeeded) {
      await fetchRegionalHighlightSlots();
    }
  };

  return {
    fetchRegionalHighlightPurchaseOptions,
    fetchRegionalHighlightPurchaseOrders,
    createRegionalHighlightPurchaseOrder,
    extendRegionalHighlightSlot,
    setRegionalHighlightAutoRenew,
  };
};
